import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { RankmiTable } from '@rankmi/rankmi-table'
import { faTags } from '@fortawesome/pro-solid-svg-icons/faTags'
import { Modal } from 'components/reusables/modal'
import { ModalButtons } from 'components/reusables/modalButtons'
import lmsProfilesList from 'api/lms-profiles/List'
import { ILmsProfileModel } from 'models/lmsProfile.model'
import { LmsProfileTypes } from 'enum/lms-profile-types.enum'
import { ProfileSelectOption } from 'components/ui/forms/course/components/forms/RequiredProfilesInput'
import { ProfileBadge } from 'components/reusables/profileBadge'
import { formatThousandsSeparator } from 'helpers/others.helper'
import styles from './styles.module.scss'

const PER_PAGE = 10

interface AddCourseProfilesModalProps {
  excludedProfileIds: Set<string>
  onClose: () => void
  onAdd: (profiles: ProfileSelectOption[]) => void
}

export const AddCourseProfilesModal = ({
  excludedProfileIds,
  onClose,
  onAdd,
}: AddCourseProfilesModalProps) => {
  const { t, i18n } = useTranslation()
  const prefix = 'administration.courses.new.form.profilesField.modal'
  const assignmentPrefix = 'configuration.lmsProfiles.list.table.assignmentMode'

  const [loading, setLoading] = useState(false)
  const [searchCriteria, setSearchCriteria] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [totalRecords, setTotalRecords] = useState(0)
  const [profiles, setProfiles] = useState<ILmsProfileModel[]>([])
  const [selectedProfiles, setSelectedProfiles] = useState<ILmsProfileModel[]>(
    []
  )

  const excludedIds = useMemo(
    () => Array.from(excludedProfileIds).map(Number).filter(Boolean),
    [excludedProfileIds]
  )

  const profileTypeLabels = useMemo(
    () => ({
      [LmsProfileTypes.participants]: t(`${assignmentPrefix}.perParticipant`),
      [LmsProfileTypes.segments]: t(`${assignmentPrefix}.perSegment`),
      [LmsProfileTypes.allUsers]: t(`${assignmentPrefix}.allUsers`),
    }),
    [t, assignmentPrefix]
  )

  const fetchProfiles = useCallback(
    async (page: number, criteria: string) => {
      setLoading(true)
      try {
        const response = await lmsProfilesList({
          page,
          searchText: criteria,
          sort: [],
          perPage: PER_PAGE,
          excludeIds: excludedIds,
        })

        setProfiles(
          response.data.map((profile) => ({ ...profile, key: profile.id }))
        )
        setTotalRecords(response.total)
      } finally {
        setLoading(false)
      }
    },
    [excludedIds]
  )

  useEffect(() => {
    setCurrentPage(1)
    setSelectedProfiles([])
    fetchProfiles(1, '')
  }, [fetchProfiles])

  const onSearch = (value: string) => {
    setSearchCriteria(value)
    setCurrentPage(1)
    fetchProfiles(1, value)
  }

  const onPaginationChange = (value: number) => {
    setCurrentPage(value)
    fetchProfiles(value, searchCriteria)
  }

  const onRowSelected = ({
    dispatcher,
    selectedRows,
    selectedRowKeys,
  }: {
    dispatcher?: string
    selectedRows?: ILmsProfileModel[]
    selectedRowKeys?: Array<string | number>
  }) => {
    if (dispatcher === 'handleUnselectAllRowsOnFilter') {
      setSelectedProfiles([])
      return
    }

    const keys = (selectedRowKeys || []).map(String)
    const merged = [...selectedProfiles, ...(selectedRows || [])]
    const addedIds: string[] = []

    setSelectedProfiles(
      merged.filter((profile) => {
        const id = String(profile.id)
        if (keys.includes(id) && !addedIds.includes(id)) {
          addedIds.push(id)
          return true
        }
        return false
      })
    )
  }

  const handleAdd = () => {
    onAdd(
      selectedProfiles.map(({ id, name, color }) => ({
        value: id,
        label: name,
        color,
      }))
    )
    onClose()
  }

  const columns = useMemo(
    () => [
      {
        title: t(`${prefix}.columns.name`),
        key: 'name',
        sorteable: true,
        render: (_: string, profile: ILmsProfileModel) => (
          <ProfileBadge name={profile.name} color={profile.color} />
        ),
      },
      {
        title: t(`${prefix}.columns.users`),
        key: 'total_users',
        sorteable: true,
        align: 'right' as const,
        render: (_: number, profile: ILmsProfileModel) =>
          formatThousandsSeparator(profile.totalUsers, i18n.language),
      },
      {
        title: t(`${prefix}.columns.assignmentMode`),
        key: 'profile_type',
        sorteable: true,
        render: (_: number, profile: ILmsProfileModel) =>
          profileTypeLabels[profile.profileType],
      },
    ],
    [t, prefix, profileTypeLabels, i18n.language]
  )

  const locale = useMemo(
    () => ({
      header: {
        rowsCount: t(`${prefix}.rowsCount`),
        rowsCount_plural: t(`${prefix}.rowsCountPlural`),
        selectedRows: t(`${prefix}.selectedRows`),
        selectedRows_plural: t(`${prefix}.selectedRows_plural`),
        clearSelection: t(`${prefix}.clearSelection`),
        search: t(`${prefix}.search`),
      },
    }),
    [t, prefix]
  )

  return (
    <Modal
      isOpen
      title={t(`${prefix}.title`)}
      maxwidth={900}
      maxheight={660}
      close={onClose}
      actions={
        <ModalButtons
          primaryText={t(`${prefix}.add`)}
          primaryAction={handleAdd}
          secondaryText={t(`${prefix}.cancel`)}
          secondaryAction={onClose}
          disablePrimaryButton={selectedProfiles.length === 0}
        />
      }
    >
      <div className={styles.modalBody}>
        <RankmiTable
          columnHeaders={columns}
          data={profiles}
          total={totalRecords}
          search={searchCriteria}
          onSearch={onSearch}
          onPaginationChange={onPaginationChange}
          currentPage={currentPage}
          onRowSelected={onRowSelected}
          selectedRows={selectedProfiles.map((profile) => profile.id)}
          countIcon={faTags}
          locale={locale}
          loading={loading}
          delaySearch
          initialRowsPerPage={PER_PAGE}
          showFooter={totalRecords > 0}
          responsive
        />
      </div>
    </Modal>
  )
}

export default AddCourseProfilesModal
